import { Fragment, useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "config/store";
import { searchActions } from "config/store/modules/search.ts";
import {
  useInteractions, useFloating, offset, useHover
} from "@floating-ui/react";
import { CategoryType } from "globals/types/models.ts";
import CategoryIcon from "components/atom/CategoryIcon.tsx";
import { useTranslation } from "react-i18next";
import { LANGS } from "globals/constants.ts";

function CategoriesNavBar() {
  //props: { showScrollToBtn: boolean, scrollToContent: () => void; }
  //const { showScrollToBtn, scrollToContent } = props;
  const categories: CategoryType[] = useAppSelector(state => state.global.categories);
  const selectedCategoryId: number | null = useAppSelector(state => state.search.selectedCategoryId);
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(true);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen && !selectedCategoryId,
    onOpenChange: setIsOpen,
    middleware: [offset(6)],
    placement: "top"
    //whileElementsMounted: autoUpdate,
  });
  //todo on hover change z index to be hover
  // Merge all the interactions into prop getters
  const { getFloatingProps, getReferenceProps } = useInteractions();

  //at first categories will be empty, so the popper will be place right, so everytime categories change, we need to recalculate
  useEffect(() => {
    context.update();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);


  const onClick = useCallback((id: number) => {
    //unselect
    if (selectedCategoryId === id) {
      dispatch(searchActions.setSelectedCategory(null));
      return;
    }

    //select
    dispatch(searchActions.setSelectedCategory(id));
  }, [dispatch, selectedCategoryId]);

  const onTooltipChange = useCallback((value: boolean) => {
    setIsOpen(!value);
  }, []);


  return (
    <div className="absolute bottom-24 left-1/2 z-10 mb-3 sm:mb-6 -translate-x-1/2">
      <div className="relative w-full"
           ref={refs.setReference}
           {...getReferenceProps()}
      >
        {/*} {showScrollToBtn && (
          <div className="absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-full block md:hidden pr-4">
            <div className="relative">
              <button
                className="h-[40px] w-[40px] min-w-[40px] sm:h-[56px] sm:w-[56px] sm:min-w-[56px] relative z-1 rounded-full bg-white shadow-lg flex flex-col justify-center items-center"
                type="button" onClick={scrollToContent}>
                <Icon name="arrow-down" className="block h-6 w-6 sm:h-8 sm:w-8 text-black" />
              </button>
              <div className="absolute z-0 animate-ping bg-white rounded-full inset-1 sm:inset-2" />
            </div>
          </div>
        )} */}
        <div className="w-full overflow-hidden rounded-lg bg-white p-1 shadow-lg">
          <div className="flex w-full flex-row items-center justify-center ">
            {categories.map(category => (
              <CategoryItem
                key={category.id}
                category={category}
                onClick={onClick}
                onTooltipChange={onTooltipChange}
                isSelected={selectedCategoryId === category.id} />
            ))}
          </div>
        </div>

        {(isOpen && !selectedCategoryId) && (
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              zIndex: 2
            }}
            {...getFloatingProps()}
          >
            <div className="rounded-full shadow bg-white px-2 py-1">
              <p className="text-xs font-medium text-black">
                {t("all")}
              </p>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}

function CategoryItem(props: {
  category: CategoryType,
  onClick: (id: number) => void,
  isSelected: boolean,
  onTooltipChange: (value: boolean) => void
}) {
  const { category, onClick, isSelected, onTooltipChange } = props;

  const currentLang = useAppSelector(state => state.global.currentLang);
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen || isSelected,
    onOpenChange: (value: boolean) => {
      onTooltipChange(value);
      setIsOpen(value);
    },
    middleware: [offset(10)],
    placement: "top"
    //whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context);

  // Merge all the interactions into prop getters
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover
  ]);

  const categoryTitle = currentLang === LANGS.FR ? category.titleFr : category.titleEn ? category.titleEn : category.titleFr;

  return (
    <Fragment>
      <button
        ref={refs.setReference}
        {...getReferenceProps()}
        onClick={() => onClick(category.id)}
        type="button"
        className={`h-[48px] w-[48px] min-w-[48px] cursor-pointer appearance-none rounded-full p-3 text-center font-medium uppercase ${isSelected ? "bg-green-150" : ""}`}>
        <CategoryIcon handle={category.handle} className={isSelected ? "text-green-500" : "text-black"} />
      </button>

      {(isOpen || isSelected) && (
        <div
          ref={refs.setFloating}
          style={{
            ...floatingStyles,
            zIndex: isOpen ? 4 : 3
          }}
          {...getFloatingProps()}
        >
          <div className="rounded-full shadow bg-white px-2 py-1">
            <p className="text-xs font-medium text-black">{categoryTitle}</p>
          </div>

        </div>
      )}
    </Fragment>

  );
}


export default CategoriesNavBar;
